import React, { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Check, Undo2, X } from "lucide-react";
import { useAiPolish } from "@/hooks/useAiPolish";
import { useAiSuggestionLog, AiSuggestionTarget } from "@/hooks/useAiSuggestionLog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { VoiceInputButton } from "./VoiceInputButton";

interface AiPolishFieldProps extends AiSuggestionTarget {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value?: string) => void;
  label?: string;
  placeholder?: string;
  /** Kort beskrivning av fältet, hjälper AI:n förstå sammanhanget */
  context?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Kompakt läge: knappen ligger bredvid etiketten och hjälptexten döljs */
  compact?: boolean;
  textareaClassName?: string;
}

/**
 * Textfält med AI-hjälp som putsar språket utan att ändra innebörden.
 * Användaren får alltid se förslaget och välja själv.
 */
export const AiPolishField: React.FC<AiPolishFieldProps> = ({
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  context,
  rows = 4,
  disabled = false,
  className,
  id,
  compact = false,
  textareaClassName,
  documentType,
  documentId,
  projectId,
  fieldKey,
  fieldLabel,
}) => {
  const { polish, isPolishing } = useAiPolish();
  const { logSuggestion, markAccepted } = useAiSuggestionLog();
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [logId, setLogId] = useState<string | null>(null);
  const [previousValue, setPreviousValue] = useState<string | null>(null);

  const target: AiSuggestionTarget = {
    documentType,
    documentId,
    projectId,
    fieldKey,
    fieldLabel: fieldLabel ?? label,
  };

  const handlePolish = async () => {
    const original = value;
    const result = await polish(value, context ?? label);
    if (!result) return;

    if (!result.changed) {
      toast.success("Texten ser redan bra ut", {
        description: "AI:n hittade inget att förbättra.",
      });
      return;
    }
    setSuggestion(result.polished);
    const id = await logSuggestion(target, original, result.polished);
    setLogId(id);
  };

  const acceptSuggestion = () => {
    if (suggestion === null) return;
    setPreviousValue(value);
    onChange(suggestion);
    setSuggestion(null);
    onBlur?.(suggestion);
    void markAccepted(logId);
    toast.success("Förslaget infogat");
  };

  const undo = () => {
    if (previousValue === null) return;
    const restored = previousValue;
    onChange(restored);
    setPreviousValue(null);
    onBlur?.(restored);
  };

  const appendDictated = (dictated: string) => {
    const next = value.trim() ? `${value.trim()}\n${dictated}` : dictated;
    onChange(next);
    onBlur?.(next);
  };

  const voiceButton = (
    <VoiceInputButton onText={appendDictated} disabled={disabled || isPolishing} compact={compact} />
  );

  const polishButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handlePolish}
      disabled={disabled || isPolishing || !value.trim()}
      className={compact ? "h-7" : undefined}
    >
      {isPolishing ? (
        <Loader2 className={cn("animate-spin", compact ? "h-3 w-3 mr-1" : "h-4 w-4 mr-1.5")} />
      ) : (
        <Sparkles className={compact ? "h-3 w-3 mr-1" : "h-4 w-4 mr-1.5"} />
      )}
      {isPolishing ? "Putsar…" : "Snygga till med AI"}
    </Button>
  );


  return (
    <div className={cn("space-y-2", className)}>
      {compact ? (
        (label || true) && (
          <div className="flex items-center justify-between gap-2">
            {label ? (
              <Label htmlFor={id} className="text-sm font-medium">
                {label}
              </Label>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-1.5">
              {voiceButton}
              {polishButton}
            </div>
          </div>
        )
      ) : (
        label && <Label htmlFor={id}>{label}</Label>
      )}

      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onBlur?.()}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled || isPolishing}
        className={textareaClassName}
      />

      {!compact && (
        <div className="flex flex-wrap items-center gap-2">
          {voiceButton}
          {polishButton}

          {previousValue !== null && suggestion === null && (
            <Button type="button" variant="ghost" size="sm" onClick={undo}>
              <Undo2 className="h-4 w-4 mr-1.5" />
              Ångra
            </Button>
          )}

          <span className="text-xs text-muted-foreground">
            Tala in eller skriv – AI:n rättar bara språket, inget innehåll läggs till.
          </span>
        </div>
      )}

      {compact && previousValue !== null && suggestion === null && (
        <Button type="button" variant="ghost" size="sm" onClick={undo} className="h-7">
          <Undo2 className="h-3 w-3 mr-1" />
          Ångra
        </Button>
      )}

      {suggestion !== null && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3">
          <p className="text-xs font-medium text-muted-foreground">AI-förslag</p>
          <p className="text-sm whitespace-pre-wrap">{suggestion}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={acceptSuggestion}>
              <Check className="h-4 w-4 mr-1.5" />
              Använd förslaget
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setSuggestion(null)}
            >
              <X className="h-4 w-4 mr-1.5" />
              Behåll min text
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPolishField;
